import { supabase } from "./supabaseClient";
import { API_BASE_URL } from "../config";

/**
 * Authenticated fetch wrapper.
 * Automatically injects the current Supabase session token as a Bearer header.
 * Usage: apiFetch("/run", { method: "POST", body: JSON.stringify({...}) })
 */
export async function apiFetch(path, options = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
}

/**
 * Converts a fetch/network error into a user-friendly message.
 * Pass the caught error object (from fetch) or an HTTP Response.
 * lang: "de" | "en" | "fr" | "it"
 */
export function friendlyError(err, lang = "de") {
  const msgs = {
    de: {
      offline:  "Wir konnten keine Verbindung herstellen. Bitte prüfe deine Internetverbindung und versuche es erneut.",
      timeout:  "Die Anfrage dauert länger als erwartet. Bitte versuche es erneut.",
      server:   "Beim Laden ist ein Fehler aufgetreten. Bitte versuche es in wenigen Minuten erneut.",
    },
    en: {
      offline:  "We could not establish a connection. Please check your internet connection and try again.",
      timeout:  "The request is taking longer than expected. Please try again.",
      server:   "An error occurred while loading. Please try again in a few minutes.",
    },
    fr: {
      offline:  "Nous n'avons pas pu établir de connexion. Vérifie ta connexion internet et réessaie.",
      timeout:  "La requête prend plus de temps que prévu. Réessaie s'il te plaît.",
      server:   "Une erreur est survenue lors du chargement. Réessaie dans quelques minutes.",
    },
    it: {
      offline:  "Non è stato possibile stabilire una connessione. Controlla la tua connessione internet e riprova.",
      timeout:  "La richiesta sta richiedendo più tempo del previsto. Riprova.",
      server:   "Si è verificato un errore durante il caricamento. Riprova tra qualche minuto.",
    },
  };
  const m = msgs[lang] || msgs.de;

  // Network-level errors (fetch threw)
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes("failed to fetch") || msg.includes("networkerror") || !navigator.onLine)
      return m.offline;
    if (msg.includes("timeout") || msg.includes("aborted"))
      return m.timeout;
  }

  // HTTP status errors
  const status = err?.status ?? err?.response?.status;
  if (status >= 500) return m.server;
  if (status === 408) return m.timeout;

  return m.server;
}
